import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Avatar, Button, message } from 'antd';
import { UserOutlined, KeyOutlined, EditOutlined, CameraOutlined, LoadingOutlined } from '@ant-design/icons';
import { MainColor } from '../../style/styleValue';
import { UserInfo } from './types';
import { postProfileImage } from '../../api/user';
import { useMessages } from '../../i18n';

const ko = {
  imageOnly: '이미지 파일만 업로드 가능합니다.',
  tooLarge: '파일 크기는 5MB 이하여야 합니다.',
  imageChanged: '프로필 이미지가 변경되었습니다.',
  uploadFailed: '이미지 업로드에 실패했습니다.',
  defaultName: '사용자',
  imageHint: '프로필 사진을 클릭하여 변경',
  name: '이름',
  edit: '수정',
  changeId: '아이디 변경',
  changePassword: '비밀번호 변경',
};

const en: typeof ko = {
  imageOnly: 'Only image files can be uploaded.',
  tooLarge: 'The file must be 5MB or smaller.',
  imageChanged: 'Your profile picture has been changed.',
  uploadFailed: 'Failed to upload the image.',
  defaultName: 'User',
  imageHint: 'Click the photo to change it',
  name: 'Name',
  edit: 'Edit',
  changeId: 'Change Username',
  changePassword: 'Change Password',
};

interface ProfileCardProps {
  userInfo: UserInfo;
  onOpenModal: (type: 'name' | 'id' | 'password') => void;
  profileImageUrl?: string;
  onProfileImageChange?: (url: string) => void;
}

const ProfileCard = ({ userInfo, onOpenModal, profileImageUrl, onProfileImageChange }: ProfileCardProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const t = useMessages({ ko, en });

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      message.error(t.imageOnly);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      message.error(t.tooLarge);
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await postProfileImage(formData);

      const localUrl = URL.createObjectURL(file);
      onProfileImageChange?.(localUrl);
      message.success(t.imageChanged);
    } catch (error) {
      message.error(t.uploadFailed);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <DashboardCard>
      <ProfileHeader>
        <AvatarWrapper onClick={handleAvatarClick}>
          {isUploading ? (
            <Avatar
              size={72}
              icon={<LoadingOutlined />}
              style={{ backgroundColor: MainColor || '#17375f' }}
            />
          ) : (
            <Avatar
              size={72}
              src={profileImageUrl || undefined}
              icon={!profileImageUrl ? <UserOutlined /> : undefined}
              style={{ backgroundColor: MainColor || '#17375f' }}
            />
          )}
          <AvatarOverlay>
            <CameraOutlined />
          </AvatarOverlay>
        </AvatarWrapper>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <ProfileGreeting>
          <strong>{userInfo.name || t.defaultName}</strong>
          <ProfileImageHint>{t.imageHint}</ProfileImageHint>
        </ProfileGreeting>
      </ProfileHeader>

      <InfoList>
        <InfoItem>
          <div className="label">{t.name}</div>
          <div className="value">
            {userInfo.name}
            <EditButton onClick={() => onOpenModal('name')}><EditOutlined /> {t.edit}</EditButton>
          </div>
        </InfoItem>
      </InfoList>

      <IdPasswordChangeBtn onClick={() => onOpenModal('id')} style={{ marginBottom: '14px' }}>
        <UserOutlined /> {t.changeId}
      </IdPasswordChangeBtn>
      <IdPasswordChangeBtn onClick={() => onOpenModal('password')}>
        <KeyOutlined /> {t.changePassword}
      </IdPasswordChangeBtn>
    </DashboardCard>
  );
};

const DashboardCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const AvatarWrapper = styled.div`
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 50%;

  &:hover > div:last-child {
    opacity: 1;
  }
`;

const AvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  font-size: 20px;
`;

const ProfileGreeting = styled.div`
  font-size: 16px;
  color: #555;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-size: 20px;
    color: #17375f;
  }
`;

const ProfileImageHint = styled.span`
  font-size: 12px;
  color: #aaa;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 13px;
    font-weight: 600;
    color: #888;
  }

  .value {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    color: #333;
    font-weight: 500;
    background: #fafafa;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid #eee;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #1890ff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #e6f7ff;
    color: #0050b3;
  }
`;

const IdPasswordChangeBtn = styled(Button)`
  width: 100%;
  height: 44px;
  border-color: #d9d9d9;
  color: #555;
  font-weight: 500;
  border-radius: 8px;

  &:hover, &:focus {
    color: #17375f !important;
    border-color: #17375f !important;
  }
`;

export default ProfileCard;